import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { slugIsReserved } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R-38 · the workspace is the first segment of every address.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY WORKSPACE-SCOPED ADDRESS IS BUILT HERE, AND A BARE `'/tasks'` IS A LINT ERROR.**
 * There were sixty absolute path literals across seventeen files before this: `navigate('/tasks')`,
 * `to="/projects"`, `` navigate(`/contracts/${code}`) ``. Prefixing them one at a time would have
 * worked exactly once — the next screen reintroduces the bug, and the bug is silent, because
 * `/tasks` under R-38 is not a 404. It matches `/:workspace` with `workspace = 'tasks'`, so the app
 * renders a workspace-not-found for a screen that exists. `eslint.config.mjs` bans the literal so
 * the compiler is what catches it rather than somebody's memory.
 *
 * ⚠️ **The signed-out screens are NOT scoped and must not be** — `/sign-in`, `/sign-up`,
 * `/invite/:token`, `/auth/*`, the password-reset pair — plus `/welcome`, `/suspended` and
 * `/specimen`. A person reaching those has no workspace to name. `RESERVED_WORKSPACE_SLUGS` in
 * `@forecastify/shared` is that list, and `routes.test.tsx` asserts the two agree, so a new root
 * route cannot ship without either joining the list or failing the build.
 */

/**
 * The route parameter's name, and the pattern `routes.tsx` registers.
 *
 * ⚠️ One constant rather than the string `':workspace'` in two files: `useParams()` reads the name
 * and the route table writes it, and a typo makes `useWorkspaceSlug()` return `undefined` on every
 * screen — which presents as "no workspace" rather than as a mistake in a path.
 */
export const WORKSPACE_PARAM = 'workspace';

/** The prefix a scoped route carries. */
export const WORKSPACE_PREFIX = `/:${WORKSPACE_PARAM}`;

/**
 * A route pattern for `routes.tsx`: `scoped('/tasks')` is `/:workspace/tasks`.
 *
 * ⚠️ **`'/'` becomes the prefix alone rather than `/:workspace/`**, because a trailing slash makes
 * `matchPath` and `location.pathname === route.path` disagree, and `app.tsx` records at length that
 * the equality version shipped once and broke every parameterised screen's sidebar highlight.
 */
export function scoped(path: string): string {
  return path === '/' ? WORKSPACE_PREFIX : `${WORKSPACE_PREFIX}${path}`;
}

/**
 * A concrete address inside a named workspace: `workspacePath('delivery', '/tasks')`.
 *
 * ⚠️ **The slug is not encoded, deliberately.** `0003`'s CHECK constrains it to
 * `[a-z0-9-]`, so there is nothing for `encodeURIComponent` to do — and running it anyway would
 * hide the one case worth failing loudly on: a slug that somehow is not URL-safe means the
 * constraint is gone.
 */
export function workspacePath(slug: string, path: string): string {
  return path === '/' ? `/${slug}` : `/${slug}${path}`;
}

/**
 * The workspace slug in the current address, or `null` on a route that has none.
 *
 * ⚠️⚠️ **FROM `useLocation()`, NOT `useParams()` — AND THE PARAMS VERSION SHIPPED FIRST AND WAS
 * WRONG.** `Chrome` builds the sidebar ABOVE its own `<Routes>`, which is deliberate (the header
 * slot has to survive a route change), and `useParams()` reads the router's MATCH — so inside
 * `Chrome` it is **always empty**, on every screen. Every sidebar link came out as `/tasks` rather
 * than `/delivery/tasks`, and because a bare `/tasks` is *rescued* by
 * `resolveWorkspaceAddress` the links still worked: one redirect per click, the address bar
 * flickering through a URL nobody wrote, and the reachability gate the only thing that could see
 * it. The catch-all made it worse — `path="*"` captures no `:workspace` at all — so a 404 page's
 * whole sidebar lost the prefix.
 *
 * The first path segment is the same fact without the match dependency, and it is available to any
 * component under the router.
 *
 * ⚠️ **A RESERVED word is `null`, not a workspace.** On `/sign-in` the first segment is `sign-in`,
 * and returning it would let a caller build `/sign-in/account`. `RESERVED_WORKSPACE_SLUGS` is
 * exactly the list of first segments that are routes rather than workspaces — the same list the
 * database refuses as a slug, which is what makes this check total rather than a guess.
 *
 * ⚠️ **`null` rather than `''`**, so a caller cannot build `//tasks` by interpolating an empty
 * string and get an address that looks nearly right.
 */
export function useWorkspaceSlug(): string | null {
  const { pathname } = useLocation();
  const first = pathname.split('/').find((segment) => segment !== '');

  if (first === undefined || slugIsReserved(first)) return null;
  return first;
}

/**
 * Build an address inside the workspace the person is currently in.
 *
 * ⚠️⚠️ **IT RETURNS THE PATH UNCHANGED WHEN THERE IS NO WORKSPACE IN THE ADDRESS, RATHER THAN
 * THROWING.** The alternative is a screen that renders on an unscoped route and crashes the moment
 * it builds a link — and the screens this is called from are the scoped ones, so the case is a
 * component rendered somewhere new rather than a state a person can reach. Degrading to the bare
 * path keeps the tree alive; the reachability gate is what catches the address being wrong.
 */
export function useWorkspacePath(): (path: string) => string {
  const slug = useWorkspaceSlug();
  return useCallback((path: string) => (slug === null ? path : workspacePath(slug, path)), [slug]);
}

/**
 * `navigate`, with the workspace prefix applied.
 *
 * ⚠️⚠️ **THIS IS WHAT THE SIXTY CALL SITES BECAME, AND THE SHAPE IS DELIBERATE.** It takes the same
 * workspace-relative path they already passed — `go('/tasks/HSO-1')` — so converting a screen is a
 * rename rather than a rewrite, and a reader comparing against the mockup still sees the address
 * the mockup draws. The prefix is applied in one function instead of being interpolated in sixty.
 *
 * ⚠️ `void`-returning, because every existing call site was `void navigate(...)`: react-router's
 * navigate returns a promise nobody awaited, and `@typescript-eslint/no-floating-promises` made
 * each of those sites carry a `void`. Swallowing it here removes sixty of them.
 */
export function useWorkspaceNavigate(): (path: string, options?: { replace?: boolean }) => void {
  const navigate = useNavigate();
  const at = useWorkspacePath();

  return useCallback(
    (path: string, options?: { replace?: boolean }) => {
      void navigate(at(path), options);
    },
    [navigate, at],
  );
}

/**
 * A back arrow: return to where the person came from, or — when the app has no earlier entry in
 * this tab — to `fallback`, a workspace-relative path like the ones `go` takes.
 *
 * ⚠️⚠️ **HISTORY FIRST, AND THE PRODUCT OWNER ASKED FOR IT BY NAME — 14 Oct 2026.** Every full
 * page's arrow used to navigate to its own LIST: a contract opened from a project's Szerződések
 * tab came back to the global contracts list, a person opened from a task came back to People. The
 * rule now is *the back arrow always leads back to where you came from*, so it steps history, and
 * the list is only where a page with no past goes.
 *
 * ⚠️ **`history.state.idx`, not `history.length`.** react-router's browser history numbers its OWN
 * entries — `0` for the one the app started on, `+1` per push, kept across a reload — so `idx > 0`
 * means the entry behind this one is this app's, never the page somebody was on before they pasted
 * a link. `history.length` counts that page too (`file-route.tsx` records it as approximate), and
 * stepping onto another site from an in-app arrow is not "back" in any sense a person means. Under
 * a memory router (the tests) there is no such state, so the fallback is what runs.
 *
 * ⚠️ The previous entry may sit in another WORKSPACE when somebody switched in between. That is
 * still where they came from, and the address carries its workspace, so it renders correctly.
 *
 * ⚠️ **The fallback REPLACES.** The page being left is the one with nothing behind it; pushing
 * the list on top would leave the browser's own Back leading straight into the page just closed.
 */
export function useGoBack(): (fallback: string) => void {
  const navigate = useNavigate();
  const go = useWorkspaceNavigate();

  return useCallback(
    (fallback: string) => {
      const state: unknown = window.history.state;
      const idx =
        typeof state === 'object' && state !== null && 'idx' in state ? state.idx : undefined;
      if (typeof idx === 'number' && idx > 0) void navigate(-1);
      else go(fallback, { replace: true });
    },
    [navigate, go],
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What an address says about which workspace somebody is in.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE URL IS THE SOURCE OF TRUTH AND `localStorage` IS THE FALLBACK, WHICH IS THE WHOLE
 * INVERSION R-38 ASKS FOR.** Before this the remembered id decided, and the address described
 * nothing: `/tasks/HSO-142` meant a different task depending on a value in another browser, two
 * tabs could not sit in two workspaces, and a link was not a link to what the sender saw. The
 * remembered id now answers one narrower question — *where do I go when the address does not say* —
 * which is `/`, a fresh sign-in, and a bookmark from before this change.
 */
export type WorkspaceAddress =
  /** An address with no workspace segment, and none expected: `/sign-in`, `/welcome`, `/specimen`. */
  | { readonly kind: 'root' }
  /** The address names a workspace this person belongs to. Adopt it. */
  | { readonly kind: 'enter'; readonly slug: string }
  /** Send them somewhere else, replacing history. */
  | { readonly kind: 'redirect'; readonly to: string }
  /**
   * The first segment is not a workspace this person is in.
   *
   * ⚠️⚠️ **AND IT DELIBERATELY CANNOT SAY WHETHER THE WORKSPACE EXISTS.** CLAUDE.md rule 5: a
   * record in another workspace is `not_found`, never `permission_denied`, because existence is
   * itself information. The client is in the same position — it knows only its own memberships —
   * so one outcome covers "no such workspace" and "not yours", and the screen says one thing.
   */
  | { readonly kind: 'unknown'; readonly slug: string };

/**
 * Everything the resolution needs, and nothing about React.
 *
 * ⚠️ An object rather than six positional parameters: `(pathname, slugs, fallback, home, roots,
 * scopedPaths)` is a signature whose arguments can be swapped without a type error, and two of them
 * are `readonly string[]`.
 */
export interface WorkspaceAddressInput {
  /** `location.pathname`, verbatim. */
  readonly pathname: string;
  /** Every workspace slug this person is a member of. */
  readonly slugs: readonly string[];
  /**
   * The workspace to assume when the address names none — the remembered one, or their first.
   * `null` when they have no membership at all.
   */
  readonly fallback: string | null;
  /** Where `/` goes inside a workspace: `HOME_PATH`. */
  readonly home: string;
  /** Route patterns that stay at the root, so no workspace segment is wanted. */
  readonly rootPaths: readonly string[];
  /**
   * Route patterns carrying `WORKSPACE_PREFIX`, used to tell a pre-R-38 bookmark (`/tasks`) apart
   * from a stranger's workspace (`/acme`).
   */
  readonly scopedPaths: readonly string[];
}

/**
 * Decide what an address means, given who the person is.
 *
 * ⚠️ **Pure, and it takes the route patterns rather than importing `ROUTES`.** `routes.tsx` imports
 * `scoped` from this file, so reaching back for the table would be a cycle — and a function that
 * takes its inputs is one a test can drive without a router.
 */
export function resolveWorkspaceAddress(input: WorkspaceAddressInput): WorkspaceAddress {
  const { pathname, slugs, fallback, home, rootPaths, scopedPaths } = input;

  /* ⚠️ A root route wins outright, and it has to be asked FIRST. `/welcome` would otherwise read as
     a workspace named `welcome` — which is exactly why `RESERVED_WORKSPACE_SLUGS` refuses that slug
     rather than leaving the ambiguity to be resolved by ordering. */
  if (rootPaths.some((pattern) => matchesPattern(pattern, pathname))) return { kind: 'root' };

  const first = pathname.split('/').find((segment) => segment !== '') ?? null;

  /* Bare `/`, or a path of nothing but slashes. */
  if (first === null) {
    return fallback === null
      ? { kind: 'root' }
      : { kind: 'redirect', to: workspacePath(fallback, home) };
  }

  if (slugs.includes(first)) return { kind: 'enter', slug: first };

  /**
   * ⚠️⚠️ **A PRE-R-38 BOOKMARK IS RESCUED RATHER THAN 404'd, AND THIS BRANCH IS WHY THE SCOPED
   * PATTERNS ARE PASSED IN.** `/tasks/HSO-142` was a real address for twenty chunks; it is now
   * `/{workspace}/tasks/HSO-142`. Without this, every link anybody saved lands on
   * "no such workspace" naming a workspace called `tasks` — which reads as the product having lost
   * their data rather than as an address having moved.
   *
   * ⚠️ It only fires when the whole remaining path matches a real screen, so `/acme` — a stranger's
   * workspace, matching no route — still falls through to `unknown` rather than being redirected
   * into the caller's own workspace and rendering somebody else's screen name.
   */
  if (fallback !== null) {
    const relative = scopedPaths.map(withoutWorkspacePrefix);
    if (relative.some((pattern) => matchesPattern(pattern, pathname))) {
      /**
       * ⚠️ **The trailing slash is dropped, and it is not cosmetic.** `/tasks/` matches `/tasks`
       * here — `matchesPattern` ignores empty segments — so a naive prefix would redirect to
       * `/delivery/tasks/`. react-router would still match the route, but `app.tsx` decides the
       * sidebar's `active` row by comparing `location.pathname` against the concrete address it
       * built, and `/delivery/tasks/` is not equal to `/delivery/tasks`. The row would go
       * unhighlighted on an address the app itself produced.
       */
      const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/u, '') : pathname;
      return { kind: 'redirect', to: workspacePath(fallback, trimmed) };
    }
  }

  return { kind: 'unknown', slug: first };
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 26 Oct 2026 · a workspace whose address changed while this tab was open.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Saving a new slug re-reads the session, and from then on the list holds the new slug only — so
 * every address this tab already has (the one on screen, every entry behind the Back button) names
 * a slug the list no longer contains, and `resolveWorkspaceAddress` would call it `unknown`.
 *
 * ⚠️⚠️ **THE ORDER CANNOT BE FIXED BY NAVIGATING AT THE RIGHT MOMENT.** React Router 7 applies a
 * navigation as a transition, so the re-read list renders first, under the old address — and
 * `WorkspaceScope` swapped the whole chrome for *"no such workspace"* for a frame, unmounting the
 * very screen that saved. Remembering what the slug WAS lets the old address keep meaning the same
 * workspace while the address bar is replaced.
 *
 * ⚠️ **This tab only, and only for as long as it lives.** Links from anywhere else are not
 * redirected — the product owner's call, 26 Oct 2026; `debt.md` keeps the history table that would.
 */

/** Old slug → current slug, after the workspace list went from `before` to `after`. */
export function renamedAfter(
  renamed: ReadonlyMap<string, string>,
  before: readonly { readonly id: string; readonly slug: string }[],
  after: readonly { readonly id: string; readonly slug: string }[],
): ReadonlyMap<string, string> {
  let next: Map<string, string> | null = null;
  for (const was of before) {
    const now = after.find((entry) => entry.id === was.id);
    if (now === undefined || now.slug === was.slug) continue;
    next ??= new Map(renamed);
    /* Renamed twice: what pointed at the middle slug points at the newest. */
    for (const [old, current] of next) if (current === was.slug) next.set(old, now.slug);
    next.set(was.slug, now.slug);
  }
  if (next === null) return renamed;
  /* ⚠️ A slug that belongs to a workspace again is that workspace's, not a memory of another. */
  for (const entry of after) next.delete(entry.slug);
  return next;
}

/**
 * `renamedAfter`, kept across the session's re-reads.
 *
 * ⚠️ State adjusted during render rather than a ref written there — the pattern React documents for
 * *"information from previous renders"*: the stale pass is discarded before anything commits.
 */
export function useRenamedSlugs(
  workspaces: readonly { readonly id: string; readonly slug: string }[],
): ReadonlyMap<string, string> {
  const [seen, setSeen] = useState(workspaces);
  const [renamed, setRenamed] = useState<ReadonlyMap<string, string>>(() => new Map());
  if (seen !== workspaces) {
    setSeen(workspaces);
    const next = renamedAfter(renamed, seen, workspaces);
    if (next !== renamed) setRenamed(next);
  }
  return renamed;
}

/** `/old/tasks/HSO-1` → `/new/tasks/HSO-1`: the first segment replaced, the rest untouched. */
export function withFirstSegment(pathname: string, slug: string): string {
  const match = /^\/[^/]+/u.exec(pathname);
  return match === null ? `/${slug}` : `/${slug}${pathname.slice(match[0].length)}`;
}

/** A route pattern with its `/:workspace` prefix removed: `/:workspace/tasks` → `/tasks`. */
export function withoutWorkspacePrefix(pattern: string): string {
  if (!pattern.startsWith(WORKSPACE_PREFIX)) return pattern;
  const rest = pattern.slice(WORKSPACE_PREFIX.length);
  return rest === '' ? '/' : rest;
}

/**
 * Whether a path matches a route pattern.
 *
 * ⚠️ **A hand-rolled match rather than `matchPath`, because this runs OUTSIDE a router.** The
 * resolution happens in `Gate` before any `<Route>` exists — `matchPath` itself is importable and
 * pure, but pulling react-router into a module a unit test drives with plain strings buys nothing.
 * The pattern language here is the one `routes.tsx` actually uses: literal segments and `:params`.
 */
function matchesPattern(pattern: string, pathname: string): boolean {
  const want = pattern.split('/').filter(Boolean);
  const have = pathname.split('/').filter(Boolean);
  if (want.length !== have.length) return false;

  return want.every((segment, index) =>
    /* ⚠️ A `:param` matches any ONE non-empty segment — never several, and never an empty one, or
       `/tasks/` would match `/tasks/:key` and render a task screen with no key. */
    segment.startsWith(':') ? (have[index] ?? '') !== '' : segment === have[index],
  );
}
